import {gql} from '@apollo/client';

/**
 * KYC Hub: supplemental documents, internal notes and the activity timeline
 * for one subject — a user (`INDIVIDUAL`) or an institutional account
 * (`INSTITUTIONAL`).
 *
 * Operation names matter: mutations refetch the lists by name
 * (`refetchQueries: ['KycHubDocuments', ...]`), so every mounted list for the
 * subject refreshes without the caller knowing which ones are on screen.
 */

const HUB_PAGE = gql`
  fragment HubPage on PaginationMeta {
    page
    limit
    pages
    total
    hasPrevPage
    hasNextPage
    previous
    next
  }
`;

const HUB_DOCUMENT_FIELDS = gql`
  fragment HubDocumentFields on BusinessDocument {
    id
    userId
    institutionalAccountId
    documentType
    freeName
    beneficialOwnerId
    fileUrl
    fileName
    provenance
    providedAt
    status
    reviewerNote
    createdAt
    reviewedAt
    deletedAt
    deletionReason
    replacesDocumentId
    replacedByDocumentId
    replacedAt
    uploadedBy {
      id
      firstName
      lastName
    }
    reviewedBy {
      id
      firstName
      lastName
    }
  }
`;

const HUB_NOTE_FIELDS = gql`
  fragment HubNoteFields on KycNote {
    id
    subjectType
    subjectId
    text
    createdBy
    author {
      id
      firstName
      lastName
    }
    createdAt
    editedAt
    editedBy
    deletedAt
    deletedBy
    deletionReason
  }
`;

export const KYC_HUB_DOCUMENT_TYPES = gql`
  query KycHubDocumentTypes($input: KycHubDocumentTypesInput!) {
    kycHubDocumentTypes(input: $input) {
      __typename
      ... on ResponseWithKycDocumentTypes {
        message
        data {
          code
          en
          fr
        }
      }
      ... on Error {
        message
        statusCode
      }
    }
  }
`;

export const KYC_HUB_DOCUMENTS = gql`
  query KycHubDocuments($input: KycHubDocumentsInput!) {
    kycHubDocuments(input: $input) {
      __typename
      ... on ResponseWithKycDocuments {
        message
        data {
          ...HubDocumentFields
        }
        pagination {
          ...HubPage
        }
      }
      ... on Error {
        message
        statusCode
      }
    }
  }
  ${HUB_DOCUMENT_FIELDS}
  ${HUB_PAGE}
`;

export const KYC_HUB_ACTIVITY = gql`
  query KycHubActivity($input: KycHubActivityInput!) {
    kycHubActivity(input: $input) {
      __typename
      ... on ResponseWithKycActivities {
        message
        data {
          id
          subjectType
          subjectId
          action
          actorType
          actorId
          actor {
            id
            firstName
            lastName
          }
          details
          createdAt
        }
        pagination {
          ...HubPage
        }
      }
      ... on Error {
        message
        statusCode
      }
    }
  }
  ${HUB_PAGE}
`;

export const KYC_HUB_NOTES = gql`
  query KycHubNotes($input: KycHubNotesInput!) {
    kycHubNotes(input: $input) {
      __typename
      ... on ResponseWithKycNotes {
        message
        data {
          ...HubNoteFields
        }
        pagination {
          ...HubPage
        }
      }
      ... on Error {
        message
        statusCode
      }
    }
  }
  ${HUB_NOTE_FIELDS}
  ${HUB_PAGE}
`;

const documentMutation = (operation, field, inputType) => gql`
  mutation ${operation}($input: ${inputType}!) {
    ${field}(input: $input) {
      __typename
      ... on ResponseWithBusinessDocument {
        message
        data {
          ...HubDocumentFields
        }
      }
      ... on Error {
        message
        statusCode
      }
    }
  }
  ${HUB_DOCUMENT_FIELDS}
`;

const noteMutation = (operation, field, inputType) => gql`
  mutation ${operation}($input: ${inputType}!) {
    ${field}(input: $input) {
      __typename
      ... on ResponseWithKycNote {
        message
        data {
          ...HubNoteFields
        }
      }
      ... on Error {
        message
        statusCode
      }
    }
  }
  ${HUB_NOTE_FIELDS}
`;

export const ADD_KYC_HUB_DOCUMENT = documentMutation(
  'AddKycHubDocument',
  'addKycHubDocument',
  'AddKycHubDocumentInput',
);
export const REPLACE_KYC_HUB_DOCUMENT = documentMutation(
  'ReplaceKycHubDocument',
  'replaceKycHubDocument',
  'ReplaceKycHubDocumentInput',
);
export const DELETE_KYC_HUB_DOCUMENT = documentMutation(
  'DeleteKycHubDocument',
  'deleteKycHubDocument',
  'DeleteKycHubDocumentInput',
);
export const REVIEW_KYC_HUB_DOCUMENT = documentMutation(
  'ReviewKycHubDocument',
  'reviewKycHubDocument',
  'ReviewKycHubDocumentInput',
);
/**
 * Reclassify or rename a document: type, free name, file name and, for a
 * beneficial owner's ID, which owner it belongs to. Allowed in any KYB status
 * but not on archived or replaced files. `reason` is required and lands in the
 * activity timeline.
 */
export const UPDATE_KYC_HUB_DOCUMENT_DETAILS = documentMutation(
  'UpdateKycHubDocumentDetails',
  'updateKycHubDocumentDetails',
  'UpdateKycHubDocumentDetailsInput',
);

export const ADD_KYC_HUB_NOTE = noteMutation(
  'AddKycHubNote',
  'addKycHubNote',
  'AddKycHubNoteInput',
);
export const UPDATE_KYC_HUB_NOTE = noteMutation(
  'UpdateKycHubNote',
  'updateKycHubNote',
  'UpdateKycHubNoteInput',
);
export const DELETE_KYC_HUB_NOTE = noteMutation(
  'DeleteKycHubNote',
  'deleteKycHubNote',
  'DeleteKycHubNoteInput',
);

/**
 * Compliance risk: an admin's rating (LOW → VERY_HIGH) and preset tags for one
 * subject, with the reason behind the last change. `data: null` means not
 * assessed. Separate from verification statuses and the investor risk profile.
 */
const COMPLIANCE_RISK_RESULT = `
  __typename
  ... on ResponseWithKycComplianceRisk {
    message
    data {
      level
      tags
      reason
      updatedBy
      updatedAt
    }
  }
  ... on Error {
    message
    statusCode
  }
`;

export const KYC_HUB_COMPLIANCE_RISK = gql`
  query KycHubComplianceRisk($input: KycHubComplianceRiskInput!) {
    kycHubComplianceRisk(input: $input) {
      ${COMPLIANCE_RISK_RESULT}
    }
  }
`;

// Also clears: `level: null`, `tags: []`, and a reason.
export const SET_KYC_HUB_COMPLIANCE_RISK = gql`
  mutation SetKycHubComplianceRisk($input: SetKycHubComplianceRiskInput!) {
    setKycHubComplianceRisk(input: $input) {
      ${COMPLIANCE_RISK_RESULT}
    }
  }
`;
